import type { AnyFieldApi, AnyFieldMeta } from "@tanstack/react-form";

import {
	useFieldContext,
	useFormContext,
} from "@bidbattles/bloks/hooks/form-context";
import { LucideLoader2 } from "lucide-react";
import { type ComponentProps, useEffect, useId, useState } from "react";
import { getConfig } from "../config";
import { type LabelledRowVariant, cn, labelledRowVariants } from "../lib/ui";
import { Button, type ButtonVariantProps } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Combobox, type ComboboxProps } from "./ui/combobox";
import { Input, type InputProps, TextArea, inputWrapper } from "./ui/input";
import { Switch } from "./ui/switch";

function SubscribeButton({
	label,
	...props
}: { label: string } & ButtonVariantProps &
	Pick<ComponentProps<typeof Button>, "onClick">) {
	const form = useFormContext();
	return (
		<form.Subscribe selector={(state) => state.isSubmitting}>
			{(isSubmitting) => (
				<Button type="submit" disabled={isSubmitting} {...props}>
					{isSubmitting && <LucideLoader2 className="animate-spin" />}
					{label}
				</Button>
			)}
		</form.Subscribe>
	);
}

function ResetButton({
	label,
	onClick,
}: { label: string } & Pick<React.ComponentProps<typeof Button>, "onClick">) {
	const form = useFormContext();

	return (
		<form.Subscribe selector={(state) => state.isDirty}>
			{(isDirty) => (
				<Button
					type="reset"
					variant="outline"
					disabled={!isDirty}
					onClick={(e) => {
						form.reset();
						onClick?.(e);
					}}
				>
					{label}
				</Button>
			)}
		</form.Subscribe>
	);
}

function ErrorMessages({
	errorMap,
}: {
	errorMap: AnyFieldMeta["errorMap"];
}) {
	const maybeErrors = errorMap?.onChange;
	const errors = Array.isArray(maybeErrors) ? maybeErrors : [maybeErrors];
	const error = errors[0] ?? { message: "" };
	const message = typeof error === "string" ? error : error.message;

	return (
		<p
			key={typeof error === "string" ? error : error.message}
			className="text-red-400 mt-1 text-xs leading-3 min-h-3"
		>
			{message}
		</p>
	);
}

type InputValue<TValue> = TValue extends "date" ? Date | null : string;
function TextField({
	id: idProp,
	type = "text",
	label,
	placeholder = label,
	className,
	variant,
	...props
}: {
	type?: "text" | "password" | "number" | "date";
	label?: string;
} & Omit<InputProps, "type">) {
	const field = useFieldContext<InputValue<typeof type>>();
	const value = field.state.value;
	const _id = `textfield:${useId()}`;
	const id = idProp ?? _id;

	return (
		<div>
			{!idProp && (
				<label htmlFor={id} className="sr-only">
					{label}
				</label>
			)}
			<Input
				{...props}
				id={id}
				className={className}
				type={type}
				variant={variant}
				{...(value instanceof Date
					? { defaultValue: value.toISOString().substring(0, 10) }
					: { value: value ?? "" })}
				placeholder={placeholder}
				onBlur={field.handleBlur}
				onChange={(e) => {
					if (type === "date") {
						field.handleChange(e.target.valueAsDate);
					} else {
						field.handleChange(e.target.value);
					}
				}}
			/>
			<ErrorMessages errorMap={field.state.meta.errorMap} />
		</div>
	);
}

function FormTextArea({
	id: idProp,
	label,
	placeholder = label,
	rows = 5,
	className,
	variant,
}: {
	id?: string;
	label?: string;
	placeholder?: string;
	rows?: number;
	className?: string;
} & Pick<React.ComponentProps<typeof TextArea>, "variant">) {
	const field = useFieldContext<string>();
	const _id = `textarea:${useId()}`;
	const id = idProp ?? _id;

	return (
		<div>
			{!idProp && (
				<label htmlFor={id} className="sr-only">
					{label}
				</label>
			)}
			<TextArea
				id={id}
				variant={variant}
				className={className}
				value={field.state.value}
				onBlur={field.handleBlur}
				placeholder={placeholder}
				rows={rows}
				onChange={(e) => field.handleChange(e.target.value)}
			/>
			<ErrorMessages errorMap={field.state.meta.errorMap} />
		</div>
	);
}

function Select({
	label,
	values,
}: {
	label: string;
	values: Array<{ label: string; value: string }>;
	placeholder?: string;
}) {
	const field = useFieldContext<string>();

	return (
		<div>
			<label htmlFor={label} className="block font-bold mb-1 text-xl">
				{label}
			</label>
			<select
				name={field.name}
				value={field.state.value}
				onBlur={field.handleBlur}
				onChange={(e) => field.handleChange(e.target.value)}
				className="w-full px-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
			>
				{values.map((value) => (
					<option key={value.value} value={value.value}>
						{value.label}
					</option>
				))}
			</select>
			<ErrorMessages errorMap={field.state.meta.errorMap} />
		</div>
	);
}

function FormCombobox<Value extends string>({
	id: idProp,
	label,
	placeholder,
	...props
}: Omit<ComboboxProps<Value>, "value" | "values" | "onChange" | "multiple"> & {
	label?: string;
	id?: string;
}) {
	const field = useFieldContext<Value | null>();
	const _id = `combobox:${useId()}`;
	const id = idProp ?? _id;

	return (
		<div>
			{!idProp && (
				<label htmlFor={id} className="sr-only">
					{label}
				</label>
			)}
			<Combobox
				{...props}
				id={id}
				multiple={false}
				placeholder={placeholder}
				value={field.state.value}
				onChange={(value) => field.handleChange(value)}
				onBlur={field.handleBlur}
			/>
			<ErrorMessages errorMap={field.state.meta.errorMap} />
		</div>
	);
}

function FormMultiCombobox<Value extends string>({
	id: idProp,
	label,
	placeholder,
	...props
}: Omit<ComboboxProps<Value>, "value" | "values" | "onChange" | "multiple"> & {
	label?: string;
}) {
	const field = useFieldContext<Value[]>();
	const _id = `combobox:${useId()}`;
	const id = idProp ?? _id;

	return (
		<div>
			{!idProp && (
				<label htmlFor={id} className="sr-only">
					{label}
				</label>
			)}
			<Combobox
				{...props}
				id={id}
				multiple={true}
				placeholder={placeholder}
				values={new Set(field.state.value)}
				onChange={(value) => field.handleChange([...value.values()])}
				onBlur={field.handleBlur}
			/>
			<ErrorMessages errorMap={field.state.meta.errorMap} />
		</div>
	);
}

function FormCheckbox({
	...props
}: Omit<
	React.ComponentProps<typeof Checkbox>,
	"checked" | "onBlur" | "onCheckedChange"
>) {
	const field = useFieldContext<boolean>();
	return (
		<Checkbox
			{...props}
			checked={field.state.value}
			onBlur={field.handleBlur}
			onCheckedChange={(e) => field.handleChange(e)}
		/>
	);
}

function FormSwitch({
	...props
}: Omit<
	React.ComponentProps<typeof Switch>,
	"checked" | "onBlur" | "onCheckedChange"
>) {
	const field = useFieldContext<boolean>();
	return (
		<Switch
			{...props}
			checked={field.state.value}
			onBlur={field.handleBlur}
			onCheckedChange={(e) => field.handleChange(e)}
		/>
	);
}

function ImagePreview({
	file,
	src,
	...props
}: React.ComponentProps<"img"> & { file?: File }) {
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);

	useEffect(() => {
		if (file && file.size !== 0) {
			const blobUrl = URL.createObjectURL(file);
			setPreviewUrl(blobUrl);
			return () => URL.revokeObjectURL(blobUrl);
		}

		setPreviewUrl(src ?? null);
	}, [file, src]);

	return <img src={previewUrl ?? undefined} {...props} />;
}

function FormLabelledRow({
	variant,
	label,
	children,
	disableErrorOffset,
}: {
	label: string;
	variant?: LabelledRowVariant;
	children: (props: { id: string; label: string }) => React.ReactElement;
	disableErrorOffset?: boolean;
}) {
	const id = `form-row:${useId()}`;
	if (!variant) {
		variant = getConfig().form.labelledRowVariant;
	}

	return (
		<div
			className={cn(
				"flex flex-col gap-2 lg:grid lg:grid-cols-5 lg:gap-4 lg:items-center",
			)}
		>
			<div
				className={cn(
					"col-span-2",
					!disableErrorOffset && "lg:-mt-3",

					labelledRowVariants({ variant }),
				)}
			>
				<label htmlFor={id}>{label}</label>
			</div>
			<div className="col-span-3">{children({ id, label })}</div>
		</div>
	);
}

const FieldComponents = {
	TextField,
	TextArea: FormTextArea,
	Checkbox: FormCheckbox,
	Switch: FormSwitch,
	Combobox: FormCombobox,
	MultiCombobox: FormMultiCombobox,
	LabelledRow: FormLabelledRow,
};
const FormComponents = {
	ResetButton,
	SubscribeButton,
	ImagePreview,
	FormLabelledRow,
	LabelledRow: FormLabelledRow,
};

export { FieldComponents, FormComponents, ErrorMessages };
